import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { numberLocale } from '@/lib/locale';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/api/schema';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Tag, Package, CheckCircle, XCircle } from 'lucide-react';

type Product = components['schemas']['ProductResource'];

interface Props {
  /**
   * Kept for the `AdminMerchantDetail` caller's compatibility — `/products`
   * has no admin `merchant_id` filter yet (E-20 GAP), so this component
   * only ever shows the current session's own merchant catalog. See
   * KNOWN_ISSUES.md.
   */
  merchantId: string;
}

export default function CatalogTab({ merchantId: _merchantId }: Props) {
  const { t } = useTranslation();
  const [productSearch, setProductSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['/products'],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/products');
      if (error) throw error;
      return data;
    },
  });
  const products: Product[] = data?.data ?? [];

  const productCategories = [...new Set(products.map(p => p.category).filter(Boolean))] as string[];

  const filteredProducts = products.filter(p => {
    const matchesSearch = !productSearch ||
      (p.name ?? '').toLowerCase().includes(productSearch.toLowerCase()) ||
      (p.sku ?? '').toLowerCase().includes(productSearch.toLowerCase()) ||
      (p.barcode ?? '').toLowerCase().includes(productSearch.toLowerCase());
    const matchesCategory = !selectedCategory || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-8 text-center">{t('misc_common.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Tag size={14} />
              <span className="text-xs font-medium">{t('misc_catalog.categories')}</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{productCategories.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Package size={14} />
              <span className="text-xs font-medium">{t('misc_dash.products')}</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{products.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <CheckCircle size={14} />
              <span className="text-xs font-medium">{t('misc_common.murabaha')}</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {products.filter(p => p.is_murabaha_eligible).length}
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="categories">
        <TabsList>
          <TabsTrigger value="categories" className="gap-1.5">
            <Tag size={14} /> {t('misc_catalog.categories')} ({productCategories.length})
          </TabsTrigger>
          <TabsTrigger value="products" className="gap-1.5">
            <Package size={14} /> {t('misc_dash.products')} ({products.length})
          </TabsTrigger>
        </TabsList>

        {/* Categories */}
        <TabsContent value="categories">
          {productCategories.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              {t('misc_catalog.no_categories')}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-4">
              {productCategories.map((catName) => {
                const count = products.filter(p => p.category === catName).length;
                return (
                  <Card key={catName} className="cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => {
                      setSelectedCategory(catName);
                      const productsTab = document.querySelector('[data-value="products"]') as HTMLElement;
                      productsTab?.click();
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-sm text-foreground">{catName}</p>
                          <p className="text-xs text-muted-foreground mt-1">{t('misc_catalog.n_products', { count })}</p>
                        </div>
                        <Badge variant="secondary" className="text-xs">{count}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Products */}
        <TabsContent value="products">
          <div className="space-y-4 mt-2">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('misc_catalog.search')}
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                <Badge
                  variant={!selectedCategory ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setSelectedCategory(null)}
                >
                  {t('misc_catalog.all_n', { count: products.length })}
                </Badge>
                {productCategories.map(cat => (
                  <Badge
                    key={cat}
                    variant={selectedCategory === cat ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
                  >
                    {cat} ({products.filter(p => p.category === cat).length})
                  </Badge>
                ))}
              </div>
            </div>

            {/* Table */}
            {filteredProducts.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                {t('misc_catalog.not_found')}
              </div>
            ) : (
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('misc_common.name')}</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>{t('misc_assign.th_brand')}</TableHead>
                      <TableHead>{t('misc_pschema.f_atribut_nomi')}</TableHead>
                      <TableHead>{t('misc_pschema.f_mhik')}</TableHead>
                      <TableHead>{t('misc_pschema.f_mxik_nomi')}</TableHead>
                      <TableHead>{t('misc_pschema.f_barcode')}</TableHead>
                      <TableHead>{t('misc_pschema.f_category')}</TableHead>
                      <TableHead>{t('misc_catalog.th_price')}</TableHead>
                      <TableHead>{t('misc_pschema.f_unit')}</TableHead>
                      <TableHead className="text-center">{t('misc_common.murabaha')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProducts.map(product => (
                      <TableRow key={product.id}>
                        <TableCell className="font-medium">{product.name}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{product.sku}</TableCell>
                        <TableCell className="text-xs">{product.brend_nomi || '—'}</TableCell>
                        <TableCell className="text-xs">{product.atribut_nomi || '—'}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{product.mhik || '—'}</TableCell>
                        <TableCell className="text-xs">{product.mxik_nomi || '—'}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{product.barcode || '—'}</TableCell>
                        <TableCell>
                          {product.category ? (
                            <Badge variant="outline" className="text-xs">{product.category}</Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>{product.sale_price?.toLocaleString(numberLocale())} {t('misc_common.currency')}</TableCell>
                        <TableCell className="text-xs">{product.unit}</TableCell>

                        <TableCell className="text-center">
                          {product.is_murabaha_eligible ? (
                            <CheckCircle size={16} className="text-green-600 mx-auto" />
                          ) : (
                            <XCircle size={16} className="text-muted-foreground/40 mx-auto" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
